/**
 * Relative alpha power and the experimental engagement indicator, as agreed for the validation sessions.
 *
 * Relative alpha power = alpha-band power (8–12 Hz) / total power (1–35 Hz)
 *
 * - Epochs: 2 s of EEG every 1 s (Welch, 1 s Hann segments, 1 Hz bins), from TP9 and TP10, the Muse's
 *   posterior sites where alpha is strongest; AF7/AF8 are kept in the record but carry blinks.
 * - An epoch is usable when at least one of TP9/TP10 is gap-free, not flat, under the artefact limit and
 *   not reported as poor or off contact. Each usable epoch adds 1 s (its hop) of usable EEG.
 * - Calibration: eyes open, display comparable to gameplay, until 30 s of usable EEG are collected. Noise
 *   or poor contact simply extends it. The initial baseline is the mean relative alpha of those epochs and
 *   is kept unchanged for the whole session.
 * - Gameplay, every 30 s: average the window's usable epochs, compare with the baseline from before the
 *   window, produce the indicator, then roll the baseline: next = (1 − λ) × previous + λ × window average.
 *   A window with too little usable EEG produces no indicator and leaves the baseline as it was.
 *
 * The indicator is relative alpha suppression, (baseline − window) / baseline: alpha falls when attention
 * is engaged with a visual task, so positive values mean less alpha than baseline. It is experimental and
 * kept separate from the behavioural score.
 */
import { bandPower, welch } from "./spectrum.ts";
import { EEG_RATE, EEG_SITES, type EegBlock } from "./muse/types.ts";
import type { ContactStatus } from "./contact-quality.ts";

export const ALPHA_SETTINGS = Object.freeze({
  alphaBandHz: [8, 12] as const,
  totalBandHz: [1, 35] as const,
  sampleRate: EEG_RATE,
  epochSeconds: 2,
  hopSeconds: 1,
  welchSegmentSamples: 256,
  alphaSites: ["TP9", "TP10"] as const,
  /** Detrended peak-to-peak above this is a blink, jaw clench or movement, not alpha. */
  artifactPeakToPeakUv: 200,
  /** Standard deviation below this is a flat (disconnected or railed) channel. */
  flatStdUv: 0.5,
  /** Contact states that make a channel unusable. */
  rejectContact: ["off", "poor", "unknown"] as readonly ContactStatus[],
  calibrationUsableSeconds: 30,
  windowSeconds: 30,
  /** Less usable EEG than this in a 30 s window: no indicator, baseline unchanged. */
  minWindowUsableSeconds: 15,
  defaultLambda: 0.2,
  indicator: "relative alpha suppression: (baseline_before - window_mean) / baseline_before",
});

const EPOCH = ALPHA_SETTINGS.epochSeconds * EEG_RATE;
const HOP = ALPHA_SETTINGS.hopSeconds * EEG_RATE;
const ALPHA_IDX = ALPHA_SETTINGS.alphaSites.map((s) => EEG_SITES.indexOf(s));

export type ChannelEpoch = { site: string; relAlpha: number | null; usable: boolean; reason: string };

export type Epoch = {
  /** Epoch ms of the first and one-past-last sample, on the EEG sample clock. */
  tStart: number;
  tEnd: number;
  channels: ChannelEpoch[];
  /** Mean relative alpha of the usable alpha sites, or null when the epoch is not usable. */
  relAlpha: number | null;
  usable: boolean;
};

/** Relative alpha for one channel's epoch, with the reason it is not usable. */
export function channelEpoch(site: string, x: Float32Array, contact: ContactStatus | undefined): ChannelEpoch {
  let min = Infinity, max = -Infinity, mean = 0;
  for (let i = 0; i < x.length; i++) {
    const v = x[i]!;
    if (!Number.isFinite(v)) return { site, relAlpha: null, usable: false, reason: "gap" };
    mean += v;
  }
  mean /= x.length;
  let varSum = 0;
  for (let i = 0; i < x.length; i++) varSum += (x[i]! - mean) ** 2;
  const psd = welch(x, ALPHA_SETTINGS.welchSegmentSamples);
  const binHz = EEG_RATE / ALPHA_SETTINGS.welchSegmentSamples;
  const total = bandPower(psd, binHz, ALPHA_SETTINGS.totalBandHz);
  const relAlpha = total > 1e-12 ? bandPower(psd, binHz, ALPHA_SETTINGS.alphaBandHz) / total : null;
  // Peak-to-peak on the mean-removed signal (slow drift is small over 2 s at these thresholds).
  for (let i = 0; i < x.length; i++) { const v = x[i]! - mean; if (v < min) min = v; if (v > max) max = v; }
  let reason = "";
  if (Math.sqrt(varSum / x.length) < ALPHA_SETTINGS.flatStdUv) reason = "flat";
  else if (max - min > ALPHA_SETTINGS.artifactPeakToPeakUv) reason = "artifact";
  else if (contact && ALPHA_SETTINGS.rejectContact.includes(contact)) reason = `contact-${contact}`;
  else if (relAlpha === null) reason = "no-power";
  return { site, relAlpha, usable: !reason, reason };
}

/** Cuts the continuous EEG into overlapping epochs. A gap or clock break restarts the epoch. */
export class EpochAnalyzer {
  #buf: number[][] = EEG_SITES.map(() => []);
  #t0: number | null = null; // epoch ms of #buf[*][0]
  #sinceHop = 0;

  reset(): void {
    this.#buf = EEG_SITES.map(() => []);
    this.#t0 = null;
    this.#sinceHop = 0;
  }

  /** @param contact current contact status per site, in EEG_SITES order */
  push(block: EegBlock, contact: readonly (ContactStatus | undefined)[] = []): Epoch[] {
    if (block.gapBefore !== 0) this.reset();
    const n = block.samples[0]?.length ?? 0;
    const out: Epoch[] = [];
    for (let i = 0; i < n; i++) {
      if (this.#t0 === null) this.#t0 = block.t0 * 1000 + (i * 1000) / EEG_RATE;
      for (let c = 0; c < EEG_SITES.length; c++) this.#buf[c]!.push(block.samples[c]?.[i] ?? NaN);
      if (this.#buf[0]!.length > EPOCH) {
        for (const b of this.#buf) b.shift();
        this.#t0 += 1000 / EEG_RATE;
      }
      this.#sinceHop++;
      if (this.#buf[0]!.length === EPOCH && this.#sinceHop >= HOP) {
        this.#sinceHop = 0;
        const channels = EEG_SITES.map((site, c) => channelEpoch(site, Float32Array.from(this.#buf[c]!), contact[c]));
        const alpha = ALPHA_IDX.map((c) => channels[c]!).filter((ch) => ch.usable && ch.relAlpha !== null);
        const relAlpha = alpha.length ? alpha.reduce((s, ch) => s + ch.relAlpha!, 0) / alpha.length : null;
        out.push({ tStart: this.#t0, tEnd: this.#t0 + (EPOCH * 1000) / EEG_RATE, channels, relAlpha, usable: relAlpha !== null });
      }
    }
    return out;
  }
}

export type CalibrationResult = {
  startedAt: number;
  endedAt: number;
  /** Wall time the calibration took; more than 30 s means it was extended. */
  durationSeconds: number;
  usableSeconds: number;
  epochs: number;
  usableEpochs: number;
  /** The original baseline: mean relative alpha of the usable calibration epochs. */
  baseline: number;
  extended: boolean;
};

export type AlphaWindow = {
  index: number;
  tStart: number;
  tEnd: number;
  usableSeconds: number;
  /** Mean relative alpha of the window's usable epochs, or null when it had too little usable EEG. */
  mean: number | null;
  baselineBefore: number;
  /** (baselineBefore − mean) / baselineBefore, or null. */
  indicator: number | null;
  baselineAfter: number;
  /** The last window, cut short when gameplay ended. It never moves the baseline. */
  partial: boolean;
};

export type AlphaPhase = "idle" | "calibrating" | "calibrated" | "gameplay" | "ended";

export type AlphaSnapshot = {
  phase: AlphaPhase;
  lambda: number;
  calibrationUsableSeconds: number;
  calibrationElapsedSeconds: number;
  calibration: CalibrationResult | null;
  baseline: number | null;
  lastWindow: AlphaWindow | null;
  windows: number;
  /** Seconds into the current gameplay window. */
  windowElapsedSeconds: number;
  /** Smoothed live relative alpha (for feedback only, never for the record's indicator). */
  liveRelAlpha: number | null;
  /** Live feedback value 0–1 for the game's focus signal, or null when there is no fresh usable EEG. */
  liveFocus: number | null;
  lastUsableAt: number | null;
};

const LIVE_TAU_S = 4;
const LIVE_GAIN = 4;
const LIVE_STALE_MS = 4000;

/**
 * Calibration, rolling baseline and windows for one session. Feed it every epoch with `epoch()` and call
 * `tick(now)` about once a second so windows close on time even while no usable EEG arrives.
 */
export class AlphaSession {
  readonly lambda: number;
  #phase: AlphaPhase = "idle";
  #calStart = 0;
  #calEpochs = 0;
  #calUsable: number[] = [];
  #calibration: CalibrationResult | null = null;
  #baseline: number | null = null;
  #windowStart = 0;
  #windowEpochs: Epoch[] = [];
  #windows: AlphaWindow[] = [];
  #live: number | null = null;
  #lastUsableAt: number | null = null;
  #now = 0;
  /** Epochs arrive a little after their end; a window closes this long after its end time. */
  readonly latencyMs: number;

  constructor(opts: { lambda?: number; latencyMs?: number } = {}) {
    this.lambda = opts.lambda ?? ALPHA_SETTINGS.defaultLambda;
    if (!(this.lambda > 0 && this.lambda <= 1)) throw new RangeError("λ must be in (0, 1]");
    this.latencyMs = opts.latencyMs ?? 2500;
  }

  get phase(): AlphaPhase { return this.#phase; }
  get windows(): readonly AlphaWindow[] { return this.#windows; }
  get calibration(): CalibrationResult | null { return this.#calibration; }

  startCalibration(now: number): void {
    this.#phase = "calibrating";
    this.#calStart = now;
    this.#calEpochs = 0;
    this.#calUsable = [];
    this.#calibration = null;
    this.#baseline = null;
    this.#now = now;
  }

  /** Starts the 30 s windows (when the player begins). Requires a finished calibration. */
  startGameplay(now: number): boolean {
    if (this.#phase !== "calibrated") return false;
    this.#phase = "gameplay";
    this.#windowStart = now;
    this.#windowEpochs = [];
    return true;
  }

  /** Ends gameplay; the window in progress is recorded as partial and does not move the baseline. */
  end(now: number): void {
    if (this.#phase === "gameplay" && now > this.#windowStart) this.#closeWindow(now, true);
    this.#phase = "ended";
  }

  epoch(e: Epoch): void {
    if (e.usable && e.relAlpha !== null) {
      this.#lastUsableAt = e.tEnd;
      const a = 1 - Math.exp(-ALPHA_SETTINGS.hopSeconds / LIVE_TAU_S);
      this.#live = this.#live === null ? e.relAlpha : this.#live + a * (e.relAlpha - this.#live);
    }
    if (this.#phase === "calibrating" && e.tStart >= this.#calStart) {
      this.#calEpochs++;
      if (e.usable && e.relAlpha !== null) this.#calUsable.push(e.relAlpha);
      if (this.#calUsable.length * ALPHA_SETTINGS.hopSeconds >= ALPHA_SETTINGS.calibrationUsableSeconds) {
        const baseline = this.#calUsable.reduce((s, v) => s + v, 0) / this.#calUsable.length;
        const endedAt = Math.max(e.tEnd, this.#calStart);
        const durationSeconds = (endedAt - this.#calStart) / 1000;
        this.#calibration = {
          startedAt: this.#calStart, endedAt, durationSeconds, usableSeconds: this.#calUsable.length * ALPHA_SETTINGS.hopSeconds,
          epochs: this.#calEpochs, usableEpochs: this.#calUsable.length, baseline,
          // A clean 30 s needs 30 epochs after the first full 2 s, so allow a few seconds of start-up.
          extended: durationSeconds > ALPHA_SETTINGS.calibrationUsableSeconds + ALPHA_SETTINGS.epochSeconds + 1,
        };
        this.#baseline = baseline;
        this.#phase = "calibrated";
      }
    } else if (this.#phase === "gameplay" && e.tEnd > this.#windowStart) {
      this.#windowEpochs.push(e);
    }
  }

  /** Closes every gameplay window whose end (plus latency) has passed. Returns the windows it closed. */
  tick(now: number): AlphaWindow[] {
    this.#now = now;
    const closed: AlphaWindow[] = [];
    while (this.#phase === "gameplay" && now - this.latencyMs >= this.#windowStart + ALPHA_SETTINGS.windowSeconds * 1000) {
      closed.push(this.#closeWindow(this.#windowStart + ALPHA_SETTINGS.windowSeconds * 1000, false));
    }
    return closed;
  }

  #closeWindow(tEnd: number, partial: boolean): AlphaWindow {
    const tStart = this.#windowStart;
    const inWindow = this.#windowEpochs.filter((e) => e.tEnd > tStart && e.tEnd <= tEnd);
    this.#windowEpochs = this.#windowEpochs.filter((e) => e.tEnd > tEnd);
    const usable = inWindow.filter((e) => e.usable && e.relAlpha !== null);
    const usableSeconds = usable.length * ALPHA_SETTINGS.hopSeconds;
    const baselineBefore = this.#baseline!;
    let mean: number | null = null, indicator: number | null = null, baselineAfter = baselineBefore;
    if (usableSeconds >= ALPHA_SETTINGS.minWindowUsableSeconds) {
      mean = usable.reduce((s, e) => s + e.relAlpha!, 0) / usable.length;
      indicator = (baselineBefore - mean) / baselineBefore;
      if (!partial) baselineAfter = (1 - this.lambda) * baselineBefore + this.lambda * mean;
    }
    const w: AlphaWindow = { index: this.#windows.length + 1, tStart, tEnd, usableSeconds, mean, baselineBefore, indicator, baselineAfter, partial };
    this.#windows.push(w);
    this.#baseline = baselineAfter;
    this.#windowStart = tEnd;
    return w;
  }

  snapshot(now = this.#now): AlphaSnapshot {
    const fresh = this.#lastUsableAt !== null && now - this.#lastUsableAt <= LIVE_STALE_MS;
    const b = this.#baseline;
    const liveFocus = fresh && b && this.#live !== null ? 1 / (1 + Math.exp((-LIVE_GAIN * (b - this.#live)) / b)) : null;
    return {
      phase: this.#phase,
      lambda: this.lambda,
      calibrationUsableSeconds: this.#calibration?.usableSeconds ?? this.#calUsable.length * ALPHA_SETTINGS.hopSeconds,
      calibrationElapsedSeconds: this.#phase === "calibrating" ? Math.max(0, (now - this.#calStart) / 1000) : this.#calibration?.durationSeconds ?? 0,
      calibration: this.#calibration,
      baseline: b,
      lastWindow: this.#windows[this.#windows.length - 1] ?? null,
      windows: this.#windows.length,
      windowElapsedSeconds: this.#phase === "gameplay" ? Math.max(0, (now - this.#windowStart) / 1000) : 0,
      liveRelAlpha: this.#live,
      liveFocus,
      lastUsableAt: this.#lastUsableAt,
    };
  }
}
